import {
  FormattedRichMessage,
  isEmail,
  MutateState,
  PageProps,
} from '@instabox/consumer-web-shared'
import {
  Button,
  Card,
  FeedbackMessage,
  Heading,
  InputField,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  Text,
} from '@instabox/consumer-web-ui'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import axios from 'axios'
import { AnimatePresence, HTMLMotionProps } from 'framer-motion'
import { useRouter } from 'next/router'
import { ChangeEvent, FC, useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import AnimatedCard from '../../components/AnimatedCard/AnimatedCard'
import { ENV_CONFIG } from '../../constants/envConfig'
import styles from '../../pages/search.module.scss'
import { prefixTranslationKey } from '../../utils/prefixTranslationKey'

function fromFormValues({ id, code, token }) {
  const trimmedId = id.trim()
  const trimmedCode = code.trim()

  return {
    id: trimmedId,
    email: isEmail(trimmedCode) ? trimmedCode : '',
    phoneNumber: isEmail(trimmedCode) ? '' : trimmedCode,
    token,
  }
}

const DELIVERY_TYPE = 'returns'

const DeliveryReturn: FC<Pick<PageProps, 'content'>> = ({ content }) => {
  const {
    query: { slug },
  } = useRouter()
  const productType = Array.isArray(slug) ? slug[0] : slug
  const images = content?.filter(({ item }) => item.__typename === 'images')
  const image = images?.find(({ item }) => {
    if (item.__typename === 'images') {
      return item.image?.title?.endsWith(productType)
    }
    return false
  })

  const { formatMessage } = useIntl()
  const [mutateState, setMutateState] = useState<MutateState>(MutateState.IDLE)
  const [form, setForm] = useState({
    code: '',
    id: '',
  })
  const turnstileRef = useRef<TurnstileInstance | null>(null)
  const [currentTab, setCurrentTab] = useState(productType)
  const [mounted, setMounted] = useState(false)
  const { push } = useRouter()
  const heroRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentTab !== productType) {
      push(`/${DELIVERY_TYPE}/${currentTab}`)
    }
  }, [currentTab, push, productType])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (image?.item.__typename === 'images') {
      const url = `${ENV_CONFIG.DIRECTUS_URL}/assets/${image.item?.image?.id}`
      heroRef.current.style.setProperty('--background-image', `url(${url})`)
    }
  }, [image])

  const handleChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [name]: value,
    })
  }

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      setMutateState(MutateState.PROCESSING)

      try {
        const token = turnstileRef.current?.getResponse()

        if (!token) {
          throw new Error('Turnstile token is missing')
        }

        const { data } = await axios.post('/api/authenticate', fromFormValues({ ...form, token }))

        if (data.redirectTo) {
          window.location.href = data.redirectTo
          setMutateState(MutateState.SUCCESS)
        } else {
          setMutateState(MutateState.ERROR)
        }
        setMutateState(MutateState.SUCCESS)
      } catch (err: unknown) {
        setMutateState(MutateState.ERROR)
      }
    },
    [form],
  )

  const isCodeValid = form.code?.length >= 4
  const isIdValid = form.id?.length >= 4
  const isFormValid = isCodeValid && isIdValid

  return (
    <div className={styles.container}>
      <div className={styles.hero} ref={heroRef}>
        <FormHeading productType={productType} />
        <InstructionsCard productType={productType} />
      </div>
      <div className={styles.form}>
        <Stack gap="xxl">
          <Stack gap="xl">
            <Stack align="center" gap="lg">
              <Tabs currentTab={currentTab} onChange={value => setCurrentTab(value)}>
                <Tab id="locker">
                  <FormattedMessage id={`${DELIVERY_TYPE}.tabs.locker`} />
                </Tab>
                <Tab id="home">
                  <FormattedMessage id={`${DELIVERY_TYPE}.tabs.home`} />
                </Tab>
              </Tabs>
              <Stack gap="lg">
                <FormHeading productType={productType} />
                <Text as="div" color="white" align="center">
                  {mounted && (
                    <FormattedRichMessage
                      id={prefixTranslationKey(`${DELIVERY_TYPE}.form.subtitle`, productType)}
                    />
                  )}
                </Text>
              </Stack>
            </Stack>
            <form onSubmit={handleSubmit} noValidate>
              <Turnstile ref={turnstileRef} siteKey={ENV_CONFIG.TURNSTILE_SITE_KEY} hidden />
              <Stack gap="lg">
                <Stack className={styles.fields} gap="sm">
                  <InputField
                    label={formatMessage({ id: `${DELIVERY_TYPE}.form.input_parcel` })}
                    name="id"
                    id="id"
                    value={form.id}
                    noBorder
                    className={mutateState === MutateState.ERROR && styles.shake}
                    onChange={handleChange}
                  />
                  <InputField
                    label={formatMessage({ id: `${DELIVERY_TYPE}.form.input_hint` })}
                    name="code"
                    id="code"
                    value={form.code}
                    noBorder
                    className={mutateState === MutateState.ERROR && styles.shake}
                    onChange={handleChange}
                  />
                </Stack>
                <Stack align="center" gap="md">
                  <Button
                    type="submit"
                    variant="solid"
                    disabled={!isFormValid || mutateState === MutateState.PROCESSING}
                    isLoading={mutateState === MutateState.PROCESSING}>
                    <FormattedMessage id={`${DELIVERY_TYPE}.form.button_submit`} />
                  </Button>
                  <AnimatePresence>
                    {mutateState === MutateState.ERROR && (
                      <AnimatedCard size="sm" variant="info" {...feedbackMessageAnimation}>
                        <FeedbackMessage>
                          <FormattedMessage id={`${DELIVERY_TYPE}.form.error_message`} />
                        </FeedbackMessage>
                      </AnimatedCard>
                    )}
                  </AnimatePresence>
                </Stack>
              </Stack>
            </form>
          </Stack>
          <InstructionsCard productType={productType} />
        </Stack>
      </div>
    </div>
  )
}

export default DeliveryReturn

const FormHeading = ({ productType }: { productType: string }) => (
  <Heading color="white" align="center" fontSize="xl" className={styles.formHeading}>
    <FormattedMessage id={prefixTranslationKey(`${DELIVERY_TYPE}.form.heading`, productType)} />
  </Heading>
)

const InstructionsCard = ({ productType }: { productType: string }) => (
  <Card className={styles.instructionsCard} size="lg">
    <Stack gap="lg">
      <Heading align="center" fontSize="sm">
        <FormattedMessage
          id={prefixTranslationKey(`${DELIVERY_TYPE}.instructions.title`, productType)}
        />
      </Heading>
      <List>
        <ListItem count={1}>
          <Text as="span">
            <FormattedRichMessage
              id={prefixTranslationKey(`${DELIVERY_TYPE}.instructions.list.item_1`, productType)}
            />
          </Text>
        </ListItem>
        <ListItem count={2}>
          <Text as="span">
            <FormattedRichMessage
              id={prefixTranslationKey(`${DELIVERY_TYPE}.instructions.list.item_2`, productType)}
            />
          </Text>
        </ListItem>
        <ListItem count={3}>
          <Text as="span">
            <FormattedRichMessage
              id={prefixTranslationKey(`${DELIVERY_TYPE}.instructions.list.item_3`, productType)}
            />
          </Text>
        </ListItem>
      </List>
    </Stack>
  </Card>
)

const feedbackMessageAnimation: HTMLMotionProps<'div'> = {
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  transition: {
    duration: 0.15,
    ease: 'easeInOut',
  },
  variants: {
    hidden: { x: '100px', opacity: 0 },
    visible: { x: '0', opacity: 1 },
  },
}
